import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher } from "@/lib/hooks";
import { StatusBadge, Money } from "@/components/common";

export function ReceiptView({ inst }) {
  const { t, fdate, fdatetime } = usePrefs();
  return (
    <div className="space-y-3 text-sm" data-testid="receipt-view">
      <div className="flex justify-between border-b pb-3"><span className="text-slate-500">{t("receipt")}</span><span className="font-data" data-testid="receipt-number">{inst.receipt_number}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">{t("due")}</span><span>{fdate(inst.due_date)}{inst.kind !== "invoice" && ` · n°${inst.number}`}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">{t("amount")}</span><Money amount={inst.amount} currency={inst.currency} /></div>
      <div className="flex justify-between"><span className="text-slate-500">{t("paid")}</span><span>{fdatetime(inst.paid_at)} · {inst.method}</span></div>
      <p className="pt-3 text-[11px] italic text-slate-400">Gestimmo — Conçue par ELOUNGOU ÉTIENNE LANDRY</p>
    </div>
  );
}

export function Schedule({ leaseId }) {
  const { user } = useAuth();
  const { t, fdate } = usePrefs();
  const { data = [] } = useQuery({ queryKey: ["installments", leaseId], queryFn: () => fetcher(`/installments?lease_id=${leaseId}`), refetchInterval: 20000 });
  const [busy, setBusy] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const pay = async (inst) => {
    setBusy(inst.id);
    try {
      const { data: r } = await api.post("/payments/checkout", { installment_id: inst.id, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const tenant = user.role === "locataire";
  return (
    <div className="overflow-x-auto" data-testid={`schedule-${leaseId}`}>
      <table className="w-full min-w-[520px] text-sm">
        <thead><tr className="border-b text-left text-[10px] uppercase tracking-[0.14em] text-slate-500"><th className="py-2">N°</th><th>{t("due")}</th><th>{t("amount")}</th><th>{t("status")}</th><th /></tr></thead>
        <tbody>
          {data.map((i) => (
            <tr key={i.id} className="border-b border-slate-100" data-testid={`installment-row-${i.id}`}>
              <td className="py-2.5 font-data text-xs text-slate-400">{i.number}</td>
              <td>{fdate(i.due_date)}</td>
              <td><Money amount={i.amount} currency={i.currency} /></td>
              <td><StatusBadge value={i.status} testId={`installment-status-${i.id}`} /></td>
              <td className="text-right">
                {i.status === "paid" && <button onClick={() => setReceipt(i)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#996515]" data-testid={`receipt-button-${i.id}`}><FileText size={13} />{t("receipt")}</button>}
                {tenant && i.status !== "paid" && (
                  <button onClick={() => pay(i)} disabled={!!busy} data-testid={`pay-button-${i.id}`} className="inline-flex items-center gap-1.5 rounded-sm bg-[#0F172A] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#B8860B] disabled:opacity-50">
                    {busy === i.id ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}{t("pay")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-sm rounded-sm"><DialogHeader><DialogTitle className="font-display text-2xl">{t("receipt")}</DialogTitle></DialogHeader>{receipt && <ReceiptView inst={receipt} />}</DialogContent>
      </Dialog>
    </div>
  );
}
